import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Textarea,
  Label,
  useToast,
} from "@repo/ui";
import { Bug, Lightbulb } from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { createSupportTicket } from "@/lib/api/support-tickets";
import { ApiClientError } from "@/lib/api/client";

type TicketType = "bug_report" | "feature_request";

interface SupportTicketDialogProps {
  open: boolean;
  onClose: () => void;
  ticketType: TicketType;
}

const CONFIG = {
  bug_report: {
    title: "Report a Problem",
    description:
      "Describe the issue you're experiencing. Include steps to reproduce if possible.",
    subjectPlaceholder: "Brief summary of the issue",
    descriptionPlaceholder:
      "What happened? What did you expect to happen instead?",
    icon: Bug,
    successMessage: "Bug report submitted. Our team will look into it.",
  },
  feature_request: {
    title: "Suggest a Feature",
    description:
      "Tell us about the feature you'd like to see. We review every suggestion.",
    subjectPlaceholder: "Feature name or brief summary",
    descriptionPlaceholder:
      "What would this feature do? How would it help your workflow?",
    icon: Lightbulb,
    successMessage:
      "Feature request submitted. Thank you for helping us improve!",
  },
} as const;

const SUBJECT_MAX = 255;
const DESCRIPTION_MAX = 5000;

export function SupportTicketDialog({
  open,
  onClose,
  ticketType,
}: SupportTicketDialogProps) {
  const [subject, setSubject] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { toast } = useToast();

  const config = CONFIG[ticketType];
  const Icon = config.icon;

  const isValid = subject.trim().length > 0 && description.trim().length > 0;

  function handleClose() {
    if (isSubmitting) return;
    setSubject("");
    setDescription("");
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await createSupportTicket({
        type: ticketType,
        subject: subject.trim(),
        description: description.trim(),
      });

      toast({
        title: "Submitted",
        description: config.successMessage,
      });

      setSubject("");
      setDescription("");
      onClose();
    } catch (error) {
      const message =
        error instanceof ApiClientError
          ? error.message
          : "Something went wrong. Please try again.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Icon size={16} weight="fill" />
            </span>
            {config.title}
          </DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ticket-subject">Subject</Label>
            <Input
              id="ticket-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={config.subjectPlaceholder}
              maxLength={SUBJECT_MAX}
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="ticket-description">Description</Label>
              <span className="text-xs text-muted-foreground">
                {description.length}/{DESCRIPTION_MAX}
              </span>
            </div>
            <Textarea
              id="ticket-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={config.descriptionPlaceholder}
              maxLength={DESCRIPTION_MAX}
              disabled={isSubmitting}
              rows={5}
              className="resize-none"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Submit
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
